import {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type ClipboardEvent,
    type CSSProperties,
    type DragEvent,
    type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import {
    FileImage,
    FileSpreadsheet,
    FileText,
    FileVideo,
    GripVertical,
    ImagePlus,
    Minus,
    MoveDiagonal2,
    Paperclip,
    Plus,
    UserPlus,
    X,
    type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../ui/utils";
import { useLanguage } from "../../i18n/language-provider";
import { useChangeFlash } from "../../hooks/useChangeFlash";
import { useFloatingWindow, type ResizeEdge } from "../../hooks/useFloatingWindow";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import type { AppRoute } from "../../navigation/routes";
import {
    StrategyHelpRequestError,
    sendStrategyHelpRequest,
    type HelpRequestKind,
    type StrategyHelpStatus,
} from "../../services/strategyHelpApi";
import {
    ACTIVE_CONNECTION_CHANGED_TOAST_ID,
    activeConnectionChangedCopy,
    toastActiveConnectionChanged,
} from "../pms/ActiveConnectionChangedNotice";
import { SUPPORT_EMAIL, SUPPORT_RESPONSE_HOURS } from "./support-contact";
import { strategyHelpCopy, type StrategyHelpCopy } from "./strategy-help-copy";
import {
    HELP_ATTACHMENT_ACCEPT,
    MAX_HELP_ATTACHMENTS,
    MAX_HELP_ATTACHMENT_BYTES,
    addHelpAttachments,
    formatAttachmentSize,
    namePastedFiles,
    screenshotPlatform,
    type HelpAttachmentProblem,
} from "./help-attachments";
import { MAX_HELP_COPIES, addHelpCopies, type HelpCopyProblem } from "./help-copies";
import { AssistantChatPanel } from "./AssistantChatPanel";

/** Mirrors `MAX_HELP_MESSAGE_CHARS` on the server. */
const MAX_MESSAGE_CHARS = 4000;

/** Where the window's place and size are remembered between visits, in this browser. */
export const ASK_OPTIVO_WINDOW_STORAGE_KEY = "optivo-ui-ask-optivo-window";

/**
 * Where each edge and corner can be taken hold of to resize the window: a
 * band mostly outside its border, as a desktop window has, so it takes no
 * press from what is inside — the body's scrollbar runs down the right edge,
 * and the close button sits a few pixels from the top-right corner. The
 * window keeps 8px of screen around it, which the bands fit into. Not on a
 * phone, where the window already spans the screen and a band this thin is
 * no target for a finger.
 */
const RESIZE_EDGES: Array<{ edge: ResizeEdge; className: string }> = [
    { edge: "n", className: "-top-[6px] left-[10px] right-[10px] h-[8px] cursor-ns-resize" },
    { edge: "s", className: "-bottom-[6px] left-[10px] right-[10px] h-[8px] cursor-ns-resize" },
    { edge: "e", className: "-right-[6px] top-[10px] bottom-[10px] w-[8px] cursor-ew-resize" },
    { edge: "w", className: "-left-[6px] top-[10px] bottom-[10px] w-[8px] cursor-ew-resize" },
    { edge: "nw", className: "-left-[6px] -top-[6px] size-[12px] cursor-nwse-resize" },
    { edge: "ne", className: "-right-[6px] -top-[6px] size-[12px] cursor-nesw-resize" },
    { edge: "sw", className: "-bottom-[6px] -left-[6px] size-[12px] cursor-nesw-resize" },
    { edge: "se", className: "-bottom-[6px] -right-[6px] size-[12px] cursor-nwse-resize" },
];

/**
 * Whether the window is on the screen, tucked into the rail's "Ask Optivo"
 * with everything in it kept, or put away — which keeps what was typed too,
 * for the next time it is opened.
 */
export type AskOptivoWindowView = "open" | "minimized" | "closed";

/**
 * The ways to ask: the server's kinds of request, and the live assistant,
 * which is no request at all — it never reaches `sendStrategyHelpRequest`.
 */
type AskOptivoKind = HelpRequestKind | "chat";

/**
 * The part of the screen a phone's keyboard leaves, while the composer has
 * the focus: a fixed window is laid out against the layout viewport, which
 * the keyboard does not shrink, so the composer would sit behind it.
 */
function useVisualViewportBox(active: boolean): { top: number; height: number } | null {
    const [box, setBox] = useState<{ top: number; height: number } | null>(null);
    useEffect(() => {
        const viewport = typeof window !== "undefined" ? window.visualViewport : null;
        if (!active || !viewport) {
            setBox(null);
            return;
        }
        const read = () => setBox({ top: viewport.offsetTop, height: viewport.height });
        read();
        viewport.addEventListener("resize", read);
        viewport.addEventListener("scroll", read);
        return () => {
            viewport.removeEventListener("resize", read);
            viewport.removeEventListener("scroll", read);
        };
    }, [active]);
    return active ? box : null;
}

/** What starts a press, and what ends one: a pointer's own events and the mouse events a browser makes of a tap. */
const PRESS_STARTS = ["pointerdown", "mousedown"] as const;
const PRESS_ENDS = ["pointerup", "pointercancel", "mouseup"] as const;

/**
 * Whether the composer has the focus, as the window's layout is told it:
 * gained at once, lost only once the press that took it is over.
 *
 * The window laid out for a phone's keyboard goes back to its own box when
 * the composer loses the focus, and the composer loses it to whatever is
 * pressed next, at the press. Told at that moment, the window moved what was
 * pressed out from under the pointer before it was let go, and the press was
 * lost: at a phone's width Close, Minimise, a rating and the ways to ask did
 * nothing on the first press, by a mouse and by a tap alike (the window went
 * from 651px at 8px to 576px at 83px between press and release; Oct 2026).
 *
 * A mouse takes the focus at its press and lets go later. A tap's touch is
 * over before the browser makes its mouse events of it, and the focus moves
 * at the first of those; so a press here is a pointer's or a mouse's, and it
 * is over a task after its release, the click a release makes coming in the
 * release's own task. A blur with no press, as Tab makes, waits that one task
 * too. `drop` is for a composer taken off the screen, which is not told it
 * lost the focus.
 */
function useComposerFocus(): { focused: boolean; change: (focused: boolean) => void; drop: () => void } {
    const [focused, setFocused] = useState(false);
    const pressing = useRef(false);
    const blurWaiting = useRef(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const forget = useCallback(() => {
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = null;
    }, []);

    const settle = useCallback(() => {
        forget();
        timer.current = setTimeout(() => {
            timer.current = null;
            if (!blurWaiting.current || pressing.current) return;
            blurWaiting.current = false;
            setFocused(false);
        }, 0);
    }, [forget]);

    useEffect(() => {
        const press = () => {
            pressing.current = true;
        };
        const release = () => {
            pressing.current = false;
            if (blurWaiting.current) settle();
        };
        // On the document, and as the events go down: the press may be let go
        // anywhere, and nothing that handles it first can keep it from here.
        for (const type of PRESS_STARTS) document.addEventListener(type, press, true);
        for (const type of PRESS_ENDS) document.addEventListener(type, release, true);
        return () => {
            for (const type of PRESS_STARTS) document.removeEventListener(type, press, true);
            for (const type of PRESS_ENDS) document.removeEventListener(type, release, true);
            forget();
        };
    }, [settle, forget]);

    const change = useCallback((next: boolean) => {
        if (next) {
            blurWaiting.current = false;
            forget();
            setFocused(true);
            return;
        }
        blurWaiting.current = true;
        if (!pressing.current) settle();
    }, [settle, forget]);

    const drop = useCallback(() => {
        blurWaiting.current = false;
        forget();
        setFocused(false);
    }, [forget]);

    return { focused, change, drop };
}

/** The limits a file refusal quotes, worded for the interface. */
interface AttachmentLimits {
    count: string;
    size: string;
}

interface StrategyHelpRequestDialogProps {
    view: AskOptivoWindowView;
    onViewChange: (view: AskOptivoWindowView) => void;
    /**
     * Taken off the screen without being put away — while asking is not
     * possible (another property open, read-only access) — so it comes back
     * as it was.
     */
    hidden?: boolean;
    /** Changes when "Ask Optivo" is pressed while the window is already open: bring it forward. */
    focusRequest?: number;
    /** Where the answer goes; null when the status could not say. */
    contactEmail: string | null;
    /** The people who run the property, offered as the colleagues to copy the answer to. */
    colleagueEmails?: string[];
    onSent: (request: NonNullable<StrategyHelpStatus["openRequest"]>) => void;
    /**
     * Whether the live assistant is offered (the status's `assistantAvailable`):
     * then it is the first way to ask, and chosen when the window opens with
     * nothing typed. Without it the window is exactly the two ways it was.
     */
    assistantAvailable?: boolean;
    /** The property the assistant's conversation is about. */
    connectionId?: number | null;
    /** Leaves for a screen an assistant's answer links to; the shell decides. */
    onNavigate?: (route: AppRoute) => void;
    /** The shell's verdict on a screen, so a link it would refuse reads as text. */
    isPageAllowed?: (route: AppRoute) => boolean | "pending";
}

/** Why a file cannot go, in the operator's words. */
function attachmentProblemMessage(
    problem: HelpAttachmentProblem,
    copy: StrategyHelpCopy,
    limits: AttachmentLimits,
): string {
    switch (problem.code) {
        case "tooMany":
            return copy.errorAttachTooMany.replace("{count}", limits.count);
        case "tooLarge":
            return copy.errorAttachTooLarge.replace("{size}", limits.size);
        case "type":
            return copy.errorAttachType.replace("{name}", problem.filename);
        case "empty":
            return copy.errorAttachEmpty.replace("{name}", problem.filename);
    }
}

/** Why something typed did not become a copy, in the operator's words. */
function copyProblemMessage(problem: HelpCopyProblem, copy: StrategyHelpCopy): string {
    switch (problem.code) {
        case "invalid":
            return copy.errorCopyInvalid.replace("{email}", problem.address);
        case "own":
            return copy.errorCopyOwn;
        case "tooMany":
            return copy.errorCopyTooMany.replace("{count}", String(MAX_HELP_COPIES));
    }
}

/** A refusal in the operator's words, with the support address where it helps. */
function refusalMessage(err: unknown, copy: StrategyHelpCopy, limits: AttachmentLimits): string {
    const refusal = err instanceof StrategyHelpRequestError ? err : null;
    const filename = refusal?.filename ?? "";
    switch (refusal?.code) {
        // The window checks a copy before it is sent, so these are the server
        // reading what the window could not.
        case "HELP_CC_INVALID":
            return copyProblemMessage({ code: "invalid", address: refusal.address ?? "" }, copy);
        case "HELP_CC_TOO_MANY":
            return copyProblemMessage({ code: "tooMany" }, copy);
        case "HELP_REQUEST_LIMIT":
            return copy.errorLimit.replace("{email}", SUPPORT_EMAIL);
        case "NO_CONTACT_EMAIL":
            return copy.errorNoEmail.replace("{email}", SUPPORT_EMAIL);
        case "HELP_MESSAGE_REQUIRED":
            return copy.errorMessageRequired;
        // The dialog checks a file before it is sent, so these are the server
        // reading what the dialog could not: the bytes, or a proxy's limit.
        case "HELP_ATTACHMENTS_TOO_MANY":
            return attachmentProblemMessage({ code: "tooMany" }, copy, limits);
        case "HELP_ATTACHMENTS_TOO_LARGE":
            return attachmentProblemMessage({ code: "tooLarge" }, copy, limits);
        case "HELP_ATTACHMENT_TYPE":
            return attachmentProblemMessage({ code: "type", filename }, copy, limits);
        case "HELP_ATTACHMENT_EMPTY":
            return attachmentProblemMessage({ code: "empty", filename }, copy, limits);
        case "HELP_ATTACHMENT_UNREADABLE":
            return copy.errorAttachUnreadable.replace("{name}", filename);
        default:
            return copy.errorGeneric.replace("{email}", SUPPORT_EMAIL);
    }
}

/** The icon a listed file wears: what it shows, by its type or its name. */
function attachmentIcon(file: File): LucideIcon {
    if (file.type.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(file.name)) return FileImage;
    if (file.type.startsWith("video/") || /\.(mp4|mov|webm)$/i.test(file.name)) return FileVideo;
    if (/\.(xlsx|xls|ods|csv)$/i.test(file.name)) return FileSpreadsheet;
    return FileText;
}

/** Whether a drag carries files, rather than text dragged into the text box. */
function carriesFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

/**
 * Ask Optivo, from any page: a question answered by e-mail, or a revenue
 * study that comes back as settings to activate.
 *
 * Two kinds, because they are answered differently: a question is answered by
 * e-mail, and a revenue study comes back as a proposal the operator activates
 * in one click — so the study says so here, before anybody expects the
 * settings to change by themselves. A question has to say something; a study
 * may come bare, since it reads the property's own bookings.
 *
 * The request carries the interface language, which is the language the
 * answer is written in, and the server attaches the property and the
 * strategy pricing it — the two things a mail from a client never said.
 *
 * The answer can be copied to colleagues — the owner who decides, the
 * receptionist who sets the prices — typed, pasted as a list, or picked from
 * the people who run the property (`help-copies.ts`). The window says who got
 * a copy from what the server stored, never from what it sent: a server that
 * predates the copies stored none.
 *
 * It asks for a screenshot too, because a picture of the screen says in one
 * look which page, which night and which number the question is about: the
 * hint names the keyboard's own way to take one, and a file is taken however
 * it comes — chosen, pasted from the clipboard, or dropped anywhere on the
 * window. The files ride on the e-mail to support and nowhere else, so when
 * the server says fewer went than were sent, the operator is asked to send
 * them by e-mail rather than told everything arrived.
 *
 * **It is a window over the page, not a dialog in front of it.** A question
 * is about something on the screen, and the modal dialog this used to be hid
 * exactly that: Libor at Hotel Zámek Svijany opened Optivo in a second
 * browser window, and signed in again, to look things up in the Prices grid
 * and photograph it while he wrote (Oct 2026). So nothing behind it is dimmed,
 * locked or closed by a click: it is dragged aside by its title bar (or moved
 * with the arrow keys from the grip), resized by any edge or corner (or with
 * the arrow keys from the grip in its corner), kept whole on the screen, and
 * opens where it was left, as large as it was left (`useFloatingWindow`). A
 * window made taller gives the room to the question being written, and what
 * it holds lays itself out by the window's width rather than the screen's.
 * Minimised, it goes into the rail's
 * "Ask Optivo" rather than into a bar of its own, because every free corner
 * of the page is somebody's — the Prices page's selection actions, the
 * toasts. It sits below every modal layer (`z-40`), so a dialog the page
 * opens covers it as it covers the page.
 *
 * Whatever happens to the window, what was typed and attached is kept — it
 * lives in this component, which the rail's provider never unmounts — and
 * only a request that went drops it. Closing it keeps it too, as the dialog
 * always did: an Escape pressed by mistake must not cost a long question.
 *
 * **Where the server offers it, a third way comes first: the live assistant**
 * (`AssistantChatPanel`), an AI that answers in the window itself from the
 * property's settings and prices. It is chosen when the window opens with
 * nothing typed and nothing chosen, settled once at that moment so a status
 * read late never changes the way on screen, and falls back to the question
 * where it is no longer offered. In it the window keeps one height and only
 * the conversation scrolls; the window may be minimised, closed or put away
 * by Escape while an answer streams in, since the conversation and what is
 * being typed live outside it; and a file pasted or dropped is refused with a
 * line saying the assistant reads none. Its description says the window is
 * writing to an AI, as every turn of the assistant does.
 *
 * **The ways to ask are a switch under the title bar, in view in every
 * state.** They used to be three cards at the head of whatever scrolled — the
 * question form, the conversation — folded over a conversation under way to
 * one line reading "Other ways to ask". The first customer to use the chat
 * did not take that line for the switch and scrolled up through the whole
 * conversation to find it (Hotel Zámek Svijany, OPT-56, Oct 2026). The switch
 * stands outside everything that scrolls and is never folded. A way goes by
 * one word on it, since three whole names do not fit a row of the window, and
 * its whole name and what it does stand at the head of its form; the
 * assistant says both of itself, at the head of its conversation. A new way
 * is a segment of the switch, with its word, its name and its hint.
 */
export function StrategyHelpRequestDialog({
    view,
    onViewChange,
    hidden = false,
    focusRequest = 0,
    contactEmail,
    colleagueEmails = [],
    onSent,
    assistantAvailable = false,
    connectionId = null,
    onNavigate,
    isPageAllowed,
}: StrategyHelpRequestDialogProps) {
    const { t, language, locale } = useLanguage();
    const copy = strategyHelpCopy(t);
    const ids = useId();
    const [message, setMessage] = useState("");
    // The way the operator chose, or none yet: then the window opens on the
    // assistant where it is offered, else on the question. The default is
    // settled once, as the window opens from closed with nothing typed, so a
    // status read late never changes the way on screen.
    const [chosenKind, setChosenKind] = useState<AskOptivoKind | null>(null);
    const [defaultKind, setDefaultKind] = useState<AskOptivoKind>(() => (assistantAvailable ? "chat" : "question"));
    const [filesRefused, setFilesRefused] = useState(false);
    const composerFocus = useComposerFocus();
    const [seenView, setSeenView] = useState<AskOptivoWindowView>(view);
    if (seenView !== view) {
        setSeenView(view);
        if (view === "open" && seenView === "closed") {
            setFilesRefused(false);
            if (chosenKind === null && message.trim() === "") setDefaultKind(assistantAvailable ? "chat" : "question");
        }
    }
    const wantedKind = chosenKind ?? defaultKind;
    // The assistant chosen and no longer offered: the question stands in.
    const kind: AskOptivoKind = wantedKind === "chat" && !assistantAvailable ? "question" : wantedKind;
    const chatFocus = useRef<HTMLElement | null>(null);
    const [focusQuestion, setFocusQuestion] = useState(0);
    const phone = useMediaQuery("(max-width: 639px)");
    const [files, setFiles] = useState<File[]>([]);
    // What is attached as of the last change rather than the last render: a
    // paste and a drop can land before the window draws again, and the second
    // must add to the first.
    const attached = useRef<File[]>([]);
    const [fileProblem, setFileProblem] = useState<string | null>(null);
    const [fileOver, setFileOver] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const messageBox = useRef<HTMLTextAreaElement>(null);
    // The colleagues the answer is copied to, as of the last change for the
    // same reason as the files: a paste and a blur can land before a render.
    const [copies, setCopies] = useState<string[]>([]);
    const copied = useRef<string[]>([]);
    const [copyText, setCopyText] = useState("");
    const [copyProblem, setCopyProblem] = useState<string | null>(null);
    const [copiesOpen, setCopiesOpen] = useState(false);
    const copyBox = useRef<HTMLInputElement>(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const shown = view === "open" && !hidden;
    const floating = useFloatingWindow<HTMLDivElement>({ storageKey: ASK_OPTIVO_WINDOW_STORAGE_KEY, shown });
    const keyboardBox = useVisualViewportBox(shown && kind === "chat" && phone && composerFocus.focused && !floating.size && !floating.position);
    // A composer taken off the screen is not told it lost the focus: the
    // window put away or hidden, or another way chosen over the chat.
    const dropComposerFocus = composerFocus.drop;
    useEffect(() => {
        if (!shown || kind !== "chat") dropComposerFocus();
    }, [shown, kind, dropComposerFocus]);
    // Asked for while already open: the title bar flashes, since the window
    // may have been dragged to where the operator is not looking.
    const flashing = useChangeFlash(String(focusRequest));

    // Opened, the caret goes where the question is typed. Opened from closed,
    // the window starts over — the refusal of a previous attempt says nothing
    // about this one — while one restored from the rail is the window it was.
    const previousView = useRef<AskOptivoWindowView>("closed");
    useEffect(() => {
        const previous = previousView.current;
        previousView.current = view;
        if (view !== "open" || previous === "open") return;
        if (previous === "closed") {
            setError(null);
            setFileProblem(null);
            setCopyProblem(null);
        }
        (kind === "chat" ? chatFocus.current : messageBox.current)?.focus();
        // The way on screen is read as the window opens, not followed after.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    // Asked for, the copy box takes the caret.
    useEffect(() => {
        if (copiesOpen) copyBox.current?.focus();
    }, [copiesOpen]);

    useEffect(() => {
        if (focusRequest > 0) (kind === "chat" ? chatFocus.current : messageBox.current)?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusRequest]);

    // Sent from the assistant to the question form: the caret follows.
    useEffect(() => {
        if (focusQuestion > 0) messageBox.current?.focus();
    }, [focusQuestion]);

    // The ways to ask stand in a row of their own under the title bar, outside
    // everything that scrolls, so the way chosen never leaves the screen. What
    // a choice does take away is the body that may have held the focus — the
    // chat's composer, the question box — and Safari gives a clicked button
    // none: the focus goes to the way chosen.
    const [focusKind, setFocusKind] = useState(0);
    useLayoutEffect(() => {
        if (focusKind === 0) return;
        document.getElementById(`${ids}-${kind}`)?.focus();
        // Asked for by a choice; the way on screen is read then, not followed after.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusKind]);

    const answerLanguage = language === "cs" || language === "en"
        ? language
        : locale?.toLowerCase().startsWith("cs") ? "cs" : "en";
    const canSend = !sending && (kind === "study" || message.trim().length > 0);
    const hours = String(SUPPORT_RESPONSE_HOURS);
    const limits: AttachmentLimits = {
        count: String(MAX_HELP_ATTACHMENTS),
        size: formatAttachmentSize(MAX_HELP_ATTACHMENT_BYTES, locale),
    };

    const replaceFiles = (next: File[]) => {
        attached.current = next;
        setFiles(next);
    };

    const screenshotHint = { mac: copy.attachHintMac, other: copy.attachHintOther, touch: copy.attachHintTouch };

    const replaceCopies = (next: string[]) => {
        copied.current = next;
        setCopies(next);
    };

    /**
     * Add what the copy box holds to the copies. What is not an address, or
     * does not fit, stays in the box to be corrected — ahead of `typing`, the
     * address still being written; returns whether all of it went in, which
     * is what a send needs before it goes.
     */
    const takeCopies = (text: string, typing = ""): boolean => {
        const result = addHelpCopies(copied.current, text, contactEmail);
        replaceCopies(result.copies);
        setCopyText([result.rest, typing].filter(Boolean).join(", "));
        setCopyProblem(result.problem ? copyProblemMessage(result.problem, copy) : null);
        return result.rest === "";
    };

    // A colleague picked from the suggestions leaves the box alone: what is
    // being typed there is the operator's, and so is what it says about it.
    const pickCopy = (address: string) => {
        const result = addHelpCopies(copied.current, address, contactEmail);
        replaceCopies(result.copies);
        if (result.problem) setCopyProblem(copyProblemMessage(result.problem, copy));
    };

    const removeCopy = (address: string) => {
        replaceCopies(copied.current.filter((copiedAddress) => copiedAddress !== address));
        setCopyProblem(null);
        copyBox.current?.focus();
    };

    // A comma or a semicolon ends an address wherever it falls: typed on a
    // phone keyboard, whose keys a key handler cannot read, or put in at once
    // by dictation or an input method. What follows the last one is still
    // being written.
    const changeCopyText = (value: string) => {
        const end = Math.max(value.lastIndexOf(","), value.lastIndexOf(";"));
        if (end < 0) setCopyText(value);
        else takeCopies(value.slice(0, end + 1), value.slice(end + 1).trimStart());
    };

    const copyKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" || event.key === "," || event.key === ";") {
            event.preventDefault();
            if (copyText.trim()) takeCopies(copyText);
        } else if (event.key === "Backspace" && copyText === "" && copied.current.length > 0) {
            event.preventDefault();
            removeCopy(copied.current[copied.current.length - 1]);
        }
    };

    // A list pasted — out of a mail client, names and all — goes in at once;
    // a single word pasted is still being typed.
    const pasteCopies = (event: ClipboardEvent<HTMLInputElement>) => {
        const text = event.clipboardData?.getData("text/plain") ?? "";
        if (!/[\s,;<]/.test(text.trim())) return;
        event.preventDefault();
        takeCopies(`${copyText} ${text}`);
    };

    const attach = (incoming: File[]) => {
        if (sending || incoming.length === 0) return;
        const result = addHelpAttachments(attached.current, incoming);
        replaceFiles(result.files);
        setFileProblem(result.problem ? attachmentProblemMessage(result.problem, copy, limits) : null);
    };

    const remove = (index: number) => {
        replaceFiles(attached.current.filter((_, i) => i !== index));
        setFileProblem(null);
    };

    // A screenshot taken with Win+Shift+S is on the clipboard. A paste that
    // also carries text is left to the text box: an office app puts a picture
    // of the text beside the text itself.
    const pasteFiles = (event: ClipboardEvent) => {
        const data = event.clipboardData;
        const pasted = Array.from(data?.files ?? []);
        if (pasted.length === 0 || Array.from(data?.types ?? []).includes("text/plain")) return;
        event.preventDefault();
        // The assistant reads no file: the chat says so and keeps none.
        if (kind === "chat") {
            setFilesRefused(true);
            return;
        }
        attach(namePastedFiles(pasted, attached.current));
    };

    // Dropped anywhere on the window, since a file dropped beside the zone
    // would otherwise open in the browser in place of the app.
    const fileDropHandlers = {
        onDragEnter: (event: DragEvent) => {
            if (!carriesFiles(event)) return;
            event.preventDefault();
            if (kind !== "chat") setFileOver(true);
        },
        onDragOver: (event: DragEvent) => {
            if (carriesFiles(event)) event.preventDefault();
        },
        onDragLeave: (event: DragEvent) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFileOver(false);
        },
        onDrop: (event: DragEvent) => {
            if (!carriesFiles(event)) return;
            event.preventDefault();
            setFileOver(false);
            if (kind === "chat") setFilesRefused(true);
            else attach(Array.from(event.dataTransfer.files));
        },
    };

    // Held where it is while a request is on its way: minimised, a refusal
    // would arrive in a window nobody can see.
    const close = () => {
        if (!sending) onViewChange("closed");
    };
    const minimize = () => {
        if (!sending) onViewChange("minimized");
    };
    const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.stopPropagation();
        close();
    };

    const submit = async () => {
        if (!canSend) return;
        // An address still in the copy box goes with the request, or stops
        // it: an address the operator typed is never dropped without a word.
        if (copyText.trim() && !takeCopies(copyText)) {
            copyBox.current?.focus();
            return;
        }
        const ccEmails = copied.current;
        // The assistant is never sent as a request: its footer has no Send.
        const requestKind: HelpRequestKind = kind === "study" ? "study" : "question";
        setSending(true);
        setError(null);
        try {
            const { request, attachmentsSent } = await sendStrategyHelpRequest({
                kind: requestKind,
                message: message.trim(),
                locale: answerLanguage,
                ...(files.length > 0 ? { attachments: files } : {}),
                ...(ccEmails.length > 0 ? { ccEmails } : {}),
            });
            const email = contactEmail ?? SUPPORT_EMAIL;
            const copiedTo = request.ccEmails ?? [];
            const reply = [
                requestKind === "study"
                    ? copy.sentStudy.replace("{email}", email)
                    : copy.sentQuestion.replace("{email}", email).replace("{hours}", hours),
                copiedTo.length > 0 ? copy.sentCopies.replace("{emails}", copiedTo.join(", ")) : null,
            ].filter(Boolean).join(" ");
            if (attachmentsSent < files.length) {
                toast.warning(copy.sentTitle, {
                    description: `${reply} ${copy.sentFilesUndelivered.replace("{email}", SUPPORT_EMAIL)}`,
                });
            } else {
                toast.success(copy.sentTitle, { description: reply });
            }
            setMessage("");
            // Unchosen again: the next opening offers the assistant first.
            setChosenKind(null);
            replaceFiles([]);
            setFileProblem(null);
            replaceCopies([]);
            setCopyText("");
            setCopyProblem(null);
            setCopiesOpen(false);
            onSent(request);
            onViewChange("closed");
        } catch (err) {
            // Sent from a page loaded for another property than the one open
            // now: nothing was filed. The toast says it past the rail, which
            // the shell takes away while another property is open, and the
            // window keeps what was typed for when this one is open again.
            const refused = toastActiveConnectionChanged(err, t, {
                send: true,
                id: ACTIVE_CONNECTION_CHANGED_TOAST_ID,
            });
            setError(refused ? activeConnectionChangedCopy(t).pageDescription : refusalMessage(err, copy, limits));
        } finally {
            setSending(false);
        }
    };

    if (!shown) return null;

    const showCopies = copiesOpen || copies.length > 0;
    const own = contactEmail?.trim().toLowerCase() ?? null;
    const suggestions = colleagueEmails.filter((address) => address !== own && !copies.includes(address));

    // Each way goes by one word on the switch, and by its whole name and what
    // it does at the head of what it asks for.
    const kinds: Array<{ value: AskOptivoKind; short: string; label: string; hint: string }> = [
        ...(assistantAvailable
            ? [{ value: "chat" as const, short: copy.kindChatShort, label: copy.kindChat, hint: copy.kindChatHint }]
            : []),
        {
            value: "question",
            short: copy.kindQuestionShort,
            label: copy.kindQuestion,
            hint: copy.kindQuestionHint.replace("{hours}", hours),
        },
        {
            value: "study",
            short: copy.kindStudyShort,
            label: copy.kindStudy,
            hint: copy.kindStudyHint.replace("{hours}", hours),
        },
    ];

    const chooseKind = (value: AskOptivoKind) => {
        setChosenKind(value);
        setFocusKind((count) => count + 1);
    };

    // The switch between the ways to ask. It stands under the title bar in
    // every way and every state of the window, outside whatever scrolls: the
    // conversation, the question form.
    const waysSwitch = (
        <div data-slot="ask-optivo-ways" className="border-b border-border px-4 py-1">
            <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={kind}
                // Radix emits "" when the way chosen is pressed again; one is always chosen.
                onValueChange={(value) => {
                    if (value) chooseKind(value as AskOptivoKind);
                }}
                aria-label={copy.kindLegend}
                className="w-full"
            >
                {kinds.map((option) => (
                    <ToggleGroupItem
                        key={option.value}
                        value={option.value}
                        id={`${ids}-${option.value}`}
                        aria-describedby={`${ids}-${option.value}-hint`}
                        className="h-10 px-1 text-sm sm:h-7"
                    >
                        {option.short}
                    </ToggleGroupItem>
                ))}
            </ToggleGroup>
            {/* Read to whoever cannot see the body under the switch: the way's
                whole name and what it does. One string, since Chromium reads
                each piece of a hidden description with a space after it and
                would put one before the full stop. */}
            {kinds.map((option) => (
                <p key={option.value} id={`${ids}-${option.value}-hint`} hidden>
                    {`${option.label}. ${option.hint}`}
                </p>
            ))}
        </div>
    );

    // What the way chosen is, at the head of what it asks for. The assistant
    // says it of itself, at the head of its conversation.
    const way = kinds.find((option) => option.value === kind) ?? kinds[kinds.length - 1];
    const wayHead = (
        <div data-slot="ask-optivo-way" className="space-y-1">
            <p className="text-sm font-medium">{way.label}</p>
            <p className="text-xs text-muted-foreground">{way.hint}</p>
        </div>
    );

    const askSupportInstead = (text: string) => {
        setChosenKind("question");
        setMessage((current) => (current.trim() ? current : text));
        setFocusQuestion((count) => count + 1);
    };

    // In the chat the window keeps one height while it is not sized by hand,
    // so only the conversation scrolls; a phone's keyboard gives it the part
    // of the screen it leaves.
    const chatHeight = kind === "chat" && !floating.size;
    const windowStyle: CSSProperties | undefined = keyboardBox
        ? { ...floating.style, top: keyboardBox.top + 8, bottom: "auto", height: Math.max(0, keyboardBox.height - 16), maxHeight: "none" }
        : floating.style;

    return createPortal(
        <div
            ref={floating.windowRef}
            role="dialog"
            aria-labelledby={`${ids}-title`}
            aria-describedby={`${ids}-description`}
            data-slot="ask-optivo-window"
            className={cn(
                "fixed z-40 flex w-[min(28rem,calc(100vw-1rem))] flex-col",
                // Its own corner until it is moved; a moved window is placed by `style`.
                !floating.position && "bottom-2 right-2 sm:bottom-4 sm:right-4",
                // At its natural size it grows with what it holds, up to the
                // screen less its corner's margins; a window sized by hand is
                // held to the screen by `useFloatingWindow` instead.
                !floating.size && "max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)]",
                chatHeight && "h-[min(36rem,calc(100dvh-1rem))] sm:h-[min(36rem,calc(100dvh-2rem))]",
            )}
            style={windowStyle}
            onKeyDown={closeOnEscape}
            onPaste={pasteFiles}
            {...fileDropHandlers}
        >
            {/* The frame clips what the window holds to its rounded border;
                the resize bands sit beside it, outside that clip. */}
            <div
                data-slot="ask-optivo-window-frame"
                className="@container/ask-optivo flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
            >
                {/* The title bar is the handle: a press anywhere on it but its two
                    buttons moves the window. */}
                <div
                    data-slot="window-title-bar"
                    className={cn(
                        "flex touch-none select-none items-start gap-1 border-b border-border py-1.5 pl-1 pr-1.5",
                        floating.dragging ? "cursor-grabbing" : "cursor-grab",
                        flashing && "flash-attention",
                    )}
                    {...floating.dragHandleProps}
                >
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-10 shrink-0 cursor-[inherit] text-muted-foreground sm:size-8"
                                aria-label={copy.windowMove}
                                onKeyDown={floating.onMoveKeyDown}
                            >
                                <GripVertical aria-hidden />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-64">{copy.windowMoveHint}</TooltipContent>
                    </Tooltip>
                    <div className="min-w-0 flex-1 py-1.5 sm:py-1">
                        <h2 id={`${ids}-title`} className="text-base font-semibold leading-tight">
                            {copy.dialogTitle}
                        </h2>
                        <p id={`${ids}-description`} className="mt-0.5 text-xs text-muted-foreground">
                            {kind === "chat" ? copy.dialogDescriptionChat : copy.dialogDescription}
                        </p>
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                data-window-control
                                className="size-10 shrink-0 sm:size-8"
                                aria-label={copy.windowMinimize}
                                disabled={sending}
                                onClick={minimize}
                            >
                                <Minus aria-hidden />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-64">{copy.windowMinimizeHint}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                data-window-control
                                className="size-10 shrink-0 sm:size-8"
                                aria-label={copy.windowClose}
                                disabled={sending}
                                onClick={close}
                            >
                                <X aria-hidden />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{copy.windowClose}</TooltipContent>
                    </Tooltip>
                </div>

                {waysSwitch}

                {kind === "chat" ? (
                    <AssistantChatPanel
                        connectionId={connectionId}
                        contactEmail={contactEmail}
                        focusRef={chatFocus}
                        filesRefused={filesRefused}
                        onNavigate={onNavigate}
                        isPageAllowed={isPageAllowed}
                        onMinimize={minimize}
                        onHandedOver={onSent}
                        onAskSupport={askSupportInstead}
                        onComposerFocusChange={composerFocus.change}
                    />
                ) : (
                    <>
                    {/* Only the body scrolls, so the title bar and the send button stay
                        in reach however much the files and the question grow. */}
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-4">
                        {wayHead}

                        {/* Grows into the room a taller window makes: the question box
                            takes it, and keeps at least its four lines when there is none. */}
                        <div className="flex flex-1 flex-col gap-1.5">
                            <Label htmlFor={`${ids}-message`}>
                                {kind === "study" ? copy.messageStudy : copy.messageQuestion}
                            </Label>
                            <Textarea
                                ref={messageBox}
                                id={`${ids}-message`}
                                value={message}
                                maxLength={MAX_MESSAGE_CHARS}
                                rows={4}
                                className="min-h-24 flex-1"
                                placeholder={kind === "study" ? copy.placeholderStudy : copy.placeholderQuestion}
                                onChange={(event) => setMessage(event.target.value)}
                            />
                            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                <p className="text-xs text-muted-foreground">
                                    {contactEmail ? copy.replyTo.replace("{email}", contactEmail) : copy.replyToAccount}
                                </p>
                                {!showCopies && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="-mr-2 h-10 px-2 text-xs sm:h-7"
                                        disabled={sending}
                                        onClick={() => setCopiesOpen(true)}
                                    >
                                        <UserPlus aria-hidden />
                                        {copy.copyAdd}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {showCopies && (
                            <div className="min-w-0 space-y-1.5">
                                <Label htmlFor={`${ids}-copy`}>{copy.copyLabel}</Label>
                                {/* The box holds the copies as they stand and the one
                                    being typed: a press on its empty part types. */}
                                <div
                                    className={cn(
                                        "flex min-h-9 w-full min-w-0 flex-wrap items-center gap-1 rounded-md border bg-input-background px-1.5 py-1 transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30",
                                        copyProblem && copyText ? "border-destructive" : "border-input",
                                    )}
                                    onClick={(event) => {
                                        if (event.target === event.currentTarget) copyBox.current?.focus();
                                    }}
                                >
                                    {copies.map((address) => (
                                        <span
                                            key={address}
                                            className="inline-flex min-w-0 max-w-full items-center rounded-sm bg-secondary pl-2 text-sm text-secondary-foreground"
                                        >
                                            <span className="min-w-0 truncate" title={address}>{address}</span>
                                            <button
                                                type="button"
                                                className="inline-flex size-10 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 sm:size-6"
                                                aria-label={copy.copyRemove.replace("{email}", address)}
                                                disabled={sending}
                                                // A click keeps the caret in the box rather than
                                                // ending what is being typed there.
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => removeCopy(address)}
                                            >
                                                <X className="size-3.5" aria-hidden />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        ref={copyBox}
                                        id={`${ids}-copy`}
                                        type="text"
                                        inputMode="email"
                                        autoComplete="off"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        value={copyText}
                                        disabled={sending}
                                        placeholder={copies.length === 0 ? copy.copyPlaceholder : undefined}
                                        aria-describedby={`${ids}-copy-hint`}
                                        aria-invalid={copyProblem && copyText ? true : undefined}
                                        className="h-8 min-w-32 flex-1 bg-transparent px-1 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed md:text-sm"
                                        onChange={(event) => changeCopyText(event.target.value)}
                                        onKeyDown={copyKeyDown}
                                        onPaste={pasteCopies}
                                        onBlur={() => {
                                            if (copyText.trim()) takeCopies(copyText);
                                        }}
                                    />
                                </div>
                                <p id={`${ids}-copy-hint`} className="text-xs text-muted-foreground">
                                    {copy.copyHint.replace("{count}", String(MAX_HELP_COPIES))}
                                </p>
                                {suggestions.length > 0 && (
                                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">{copy.copySuggestions}</span>
                                        {suggestions.map((address) => (
                                            <Button
                                                key={address}
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-10 min-w-0 max-w-full px-2 text-xs sm:h-7"
                                                disabled={sending}
                                                aria-label={copy.copySuggestionAdd.replace("{email}", address)}
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => pickCopy(address)}
                                            >
                                                <Plus aria-hidden />
                                                <span className="min-w-0 truncate">{address}</span>
                                            </Button>
                                        ))}
                                    </div>
                                )}
                                {copyProblem && (
                                    <p role="alert" className="text-xs text-destructive">{copyProblem}</p>
                                )}
                            </div>
                        )}

                        {/* `min-w-0`: a file name is one unbroken line that would
                            otherwise set the column's width. */}
                        <div role="group" aria-labelledby={`${ids}-files`} className="min-w-0 space-y-2">
                            <p id={`${ids}-files`} className="text-sm font-medium">{copy.attachLabel}</p>
                            {/* By the window's own width: a window made narrow on a wide
                                screen stacks the button under the hint, as a phone does. */}
                            <div
                                className={`flex flex-col gap-3 rounded-md border border-dashed p-3 transition-colors @min-[26rem]/ask-optivo:flex-row @min-[26rem]/ask-optivo:items-center ${
                                    fileOver ? "border-primary bg-accent" : "border-border"
                                }`}
                            >
                                <ImagePlus
                                    className="hidden size-5 shrink-0 text-muted-foreground @min-[26rem]/ask-optivo:block"
                                    aria-hidden
                                />
                                <div id={`${ids}-files-hint`} className="min-w-0 flex-1 space-y-1 text-xs text-muted-foreground">
                                    <p>{screenshotHint[screenshotPlatform()]}</p>
                                    <p>
                                        {copy.attachLimits.replace("{count}", limits.count).replace("{size}", limits.size)}
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="self-start @min-[26rem]/ask-optivo:self-center"
                                    disabled={sending}
                                    aria-describedby={`${ids}-files-hint`}
                                    onClick={() => fileInput.current?.click()}
                                >
                                    <Paperclip aria-hidden />
                                    {copy.attachButton}
                                </Button>
                            </div>
                            <input
                                ref={fileInput}
                                type="file"
                                multiple
                                accept={HELP_ATTACHMENT_ACCEPT}
                                className="hidden"
                                tabIndex={-1}
                                aria-hidden
                                onChange={(event) => {
                                    attach(Array.from(event.target.files ?? []));
                                    // So the same file can be chosen again after it is removed.
                                    event.target.value = "";
                                }}
                            />
                            {files.length > 0 && (
                                <ul className="space-y-1">
                                    {files.map((file, index) => {
                                        const Icon = attachmentIcon(file);
                                        return (
                                            <li
                                                key={`${file.name}-${file.size}-${file.lastModified}`}
                                                className="flex items-center gap-2 rounded-md border border-border py-0.5 pl-2.5 pr-0.5 text-sm"
                                            >
                                                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                                                <span className="min-w-0 flex-1 truncate" title={file.name}>{file.name}</span>
                                                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                                                    {formatAttachmentSize(file.size, locale)}
                                                </span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="size-10 shrink-0 sm:size-8"
                                                    disabled={sending}
                                                    aria-label={copy.removeAttachment.replace("{name}", file.name)}
                                                    onClick={() => remove(index)}
                                                >
                                                    <X aria-hidden />
                                                </Button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {fileProblem && (
                                <p role="alert" className="text-xs text-destructive">{fileProblem}</p>
                            )}
                        </div>

                        {error && (
                            <p role="alert" className="text-sm text-destructive">
                                <span className="font-medium">{copy.failedTitle}.</span> {error}
                            </p>
                        )}
                    </div>

                    {/* Room on the right for the resize grip in the corner. */}
                    <div className="flex justify-end gap-2 border-t border-border p-3 sm:pr-7">
                        <Button variant="outline" onClick={close} disabled={sending}>
                            {t.common?.cancel ?? "Cancel"}
                        </Button>
                        <Button onClick={() => void submit()} disabled={!canSend}>
                            {sending ? copy.sending : copy.send}
                        </Button>
                    </div>
                    </>
                )}
            </div>

            {RESIZE_EDGES.map(({ edge, className }) => (
                <div
                    key={edge}
                    aria-hidden
                    data-resize-edge={edge}
                    className={cn("absolute z-10 hidden touch-none sm:block", className)}
                    {...floating.resizeHandleProps(edge)}
                />
            ))}
            {/* The corner's grip says the window can be resized, and is the
                way to do it from the keyboard. */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        aria-label={copy.windowResize}
                        className="absolute bottom-0.5 right-0.5 z-20 hidden size-5 cursor-nwse-resize touch-none items-center justify-center rounded-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:flex"
                        onKeyDown={floating.onResizeKeyDown}
                        {...floating.resizeHandleProps("se")}
                    >
                        <MoveDiagonal2 className="size-3.5" aria-hidden />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-64">{copy.windowResizeHint}</TooltipContent>
            </Tooltip>
        </div>,
        document.body,
    );
}
